import {
  getProfileActiveVouchers,
  getProfileHistoryVouchers,
} from '@/features/profile/profileActions';
import {
  activeVouchersProfileSelector,
  historyVouchersProfileSelector,
} from '@/features/profile/profileSelectors';
import { TransText } from '@/i18n/trans/text';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import VoucherSummary from '@/routes/account/vouchers/VoucherSummary';
import { useDispatch, useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { useEffect, type FC } from 'react';
import { toast } from 'react-toastify';

interface VouchersListProps {
  timeType: ProfileBookingTimeType;
}

const VouchersList: FC<VouchersListProps> = ({ timeType }) => {
  const dispatch = useDispatch();

  const isActive = timeType === 'active';

  const vouchers = useSelector(
    isActive ? activeVouchersProfileSelector : historyVouchersProfileSelector
  );

  // Refetch on every visit, not only when the store is empty: voucher
  // balances change outside this view (e.g. a purchase paid with a voucher),
  // so a cached list goes stale (BR-51922). The cached list stays on screen
  // while the refresh is in flight.
  useEffect(() => {
    const fetchVouchers = async () => {
      try {
        await dispatch(
          isActive ? getProfileActiveVouchers() : getProfileHistoryVouchers()
        ).unwrap();
      } catch (error) {
        toast.error(
          <TransText
            i18nKey={
              isActive
                ? 'errorFetchingActiveVouchers'
                : 'errorFetchingHistoryVouchers'
            }
          />
        );
      }
    };
    fetchVouchers();
  }, [dispatch, isActive]);

  if (!vouchers?.items?.length) {
    return <BookingNoItems bookingType="vouchers" timeType={timeType} />;
  }

  return (
    <div className="h-full w-full">
      <div className="grid w-full grid-cols-1 gap-4 laptop:grid-cols-2">
        {/* Keyed by code with index fallback — the backend voucher `id` is
            optional and DEV omits it (BR-51922). */}
        {vouchers.items.map((voucher, index) => (
          <VoucherSummary key={voucher.code ?? index} voucher={voucher} />
        ))}
      </div>
    </div>
  );
};

export default VouchersList;
